import type { SimEvent } from '@runkr/sim'

/**
 * Room messages besides `"i"` (input, see `input.ts`). ARCHITECTURE §4.1–4.2, PHASE1 T1.6.
 * Client → server: `ready`, `emote`, `rematch`, `start`. Server → client: `ev`, `code`, `emote`,
 * `lag`, `reward`.
 */

/** Lobby ready toggle (T1.8). Accepted and ignored by the Phase 1 room. */
export const MSG_READY = 'ready'
/** Client → server `{ id }`; server → everyone `{ player, id }` (player index). */
export const MSG_EMOTE = 'emote'
/** Private room host, in `finished`: back to `waiting` with the same clients and a new seed. */
export const MSG_REMATCH = 'rematch'
/** Private room host, in `waiting`: start the countdown (bots fill empty slots). */
export const MSG_START = 'start'
/** Server → everyone: the `SimEvent`s of one tick, sent only for ticks that had any. */
export const MSG_EVENTS = 'ev'
/** Server → private room creator: the join code. */
export const MSG_CODE = 'code'

/** Server → each human, once per second from the countdown on: its input timing (`LagMsg`). */
export const MSG_LAG = 'lag'

/** Server → one paid, still-connected human after its race was saved (`RewardMsg`, T2.3). */
export const MSG_REWARD = 'reward'

/** Highest emote id the server relays. */
export const EMOTE_MAX = 15

export interface EmoteMsg {
  id: number
}

export interface EmoteBroadcast {
  player: number
  id: number
}

export type EventsMsg = SimEvent[]

/**
 * How early the client's `"i"` inputs reached the server over the last second (T1.7 clock
 * sync, D-033). `lead` = `t − serverTick` on arrival; `t` is applied in time when `lead ≥ 0`.
 */
export interface LagMsg {
  /** Inputs accepted. */
  n: number
  /** Accepted after their tick (`lead < 0`) plus dropped as too late. */
  late: number
  /** Dropped as too early (`lead > 2`). */
  early: number
  /** Smallest and largest `lead` of the accepted inputs (0 when `n` is 0). */
  minLead: number
  maxLead: number
}

/**
 * This race's saved rewards and the profile totals after them (D-038). Sent only after a
 * successful first write of the match; the results screen shows the earned amounts from
 * `ResultSchema` either way.
 */
export interface RewardMsg {
  /** Earned this race. */
  xp: number
  coins: number
  /** Profile totals after the race (and its completed daily tasks). */
  totalXp: number
  level: number
  levelBefore: number
  /** Includes the coins of tasks completed by this race. */
  totalCoins: number
  /**
   * Daily tasks this race moved (T2.5): progress after the race, `completedNow` when it reached
   * its target just now (its coins and XP are in the totals). Empty when none moved or the
   * database has no daily tasks yet (before migration 0003); absent from older servers.
   */
  tasks?: RewardTask[]
}

export interface RewardTask {
  /** `economy.json` daily task id. */
  id: string
  progress: number
  target: number
  completedNow: boolean
}

export interface CodeMsg {
  code: string
}

/** Options for `joinOrCreate("race")` / `create("race")` / `joinById`. */
export interface RaceJoinOptions {
  /** Supabase access token (required whenever the server enforces auth, D-017). */
  token?: string
  /** Dev mode only (server without Supabase): wanted display name, 3–24 of `[A-Za-z0-9_-]`. */
  name?: string
  /** `create("race", { private: true })` makes a private room with a join code. */
  private?: boolean
  /** Join code, required to join a private room by id. */
  code?: string
  /** Runner picked in the hub (0–999, PHASE3 T3.1); a profile's equipped character wins. */
  charId?: number
  /** The equipped ride (`RideId`, T3.10); anything else rides nothing. */
  ride?: string
}

/** Private room join codes (ARCHITECTURE §5.2): no 0/O/1/I. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const ROOM_CODE_LENGTH = 4

/** WebSocket close code when the server kicks a client for input abuse (§4.1). */
export const CLOSE_KICKED = 4403

/** Room name of the party room (PHASE2 T2.7, D-040); private, at most `PARTY_MAX` members. */
export const PARTY_ROOM = 'party'
export const PARTY_MAX = 4
/** Party leader → party: queue every member into one public race (from `idle` or `racing`). */
export const MSG_QUEUE = 'queue'
/** Party leader → party: stop queueing / back to `idle`. */
export const MSG_CANCEL = 'cancel'
/** Party → each member: its reserved seat in the race room (`SeatMsg`). */
export const MSG_SEAT = 'seat'
/** Party → leader: no race could be found or made for the party (`QueueFailedMsg`). */
export const MSG_QUEUE_FAILED = 'queueFailed'

/** What a code resolves to: a private race lobby or a party (`GET /code/<CODE>`, D-040). */
export type CodeKind = 'race' | 'party'

/** `GET /code/<CODE>` body. Clients before T2.7 read `roomId` only. */
export interface CodeLookup {
  roomId: string
  kind: CodeKind
}

/** A Colyseus seat reservation (`ISeatReservation`), consumed by the SDK's `consumeSeatReservation`. */
export interface SeatReservation {
  name: string
  roomId: string
  sessionId: string
  processId?: string
  publicAddress?: string
  reconnectionToken?: string
  devMode?: boolean
}

export interface SeatMsg {
  reservation: SeatReservation
}

export interface QueueFailedMsg {
  reason: 'no-room'
}

/**
 * Options for `create("party")` / `joinById(<party>)`: the member's own race join options, from
 * the same builder as a solo race join (token, dev name, `charId`, `ride`, and whatever is added
 * later), plus the party's `code` for everyone but its creator. The party room keeps all but
 * `token`, `code` and `private` and reserves the member's race seats with them (T2.7).
 */
export type PartyJoinOptions = RaceJoinOptions

/**
 * Party member → party: its current race join options without the token (`RaceJoinOptions`),
 * replacing the ones it joined with, for its next race seats (T2.7).
 */
export const MSG_SEAT_OPTIONS = 'seatOptions'
